// Shared AG Grid wrapper, configured as a "live sheet": range/cell selection
// with copy/paste, a status bar that shows sum/avg/min/max/count for the
// selected cells, Columns + Filters tool-panel sidebars, drag-to-group on any
// column, and (when enableExport) a toolbar with quick-search + CSV/Excel export.
//
// These are AG Grid Enterprise features; without a license key they work with an
// evaluation watermark. Set REACT_APP_AG_GRID_LICENSE_KEY to remove it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import './DataGrid.css';

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);

if (process.env.REACT_APP_AG_GRID_LICENSE_KEY) {
    LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY);
}

// Row counts on the left; sum/avg/min/max/count of the selected range on the
// right (the aggregation panel populates automatically for numeric cells).
const DEFAULT_STATUS_BAR = {
    statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' },
        { statusPanel: 'agSelectedRowCountComponent', align: 'left' },
        { statusPanel: 'agAggregationComponent', align: 'right' },
    ],
};

const DEFAULT_SIDE_BAR = { toolPanels: ['columns', 'filters'] };

// Applied to every column so any of them can be grouped / used as a value /
// pivoted from the UI. Merged with (and overridable by) the caller's defaultColDef.
const BASE_DEFAULT_COL_DEF = {
    sortable: true,
    filter: true,
    resizable: true,
    enableRowGroup: true,
    enableValue: true,
    enablePivot: true,
};

// Ceiling for auto-sized columns, so one long free-text value (a campaign note,
// a case status) can't push every other column off screen. Users can still drag
// wider — this only bounds the automatic pass.
const MAX_AUTO_WIDTH = 420;

// Applies pinned bottom rows to a grid api, working around AG Grid 36.0.0 not
// repainting pinned rows when the option changes on a mounted grid — the row
// keeps its previous data (stale totals) or fails to appear; only the initial
// mount applied it, which is why a hard refresh looked fine while in-place
// updates did not. Same-count updates go through node.setData (which does
// repaint); count changes rebuild the rows via the grid option. Exported for
// grids DataGrid doesn't own, like master/detail sub-grids.
export function applyPinnedTo(api, desired) {
    if (!api || (typeof api.isDestroyed === 'function' && api.isDestroyed())) return;
    const rows = desired || [];
    const count = api.getPinnedBottomRowCount ? api.getPinnedBottomRowCount() : 0;
    if (count === rows.length && count > 0) {
        rows.forEach((data, i) => api.getPinnedBottomRow(i)?.setData(data));
    } else {
        api.setGridOption('pinnedBottomRowData', rows);
    }
}

export default function DataGrid({
    rowData,
    columnDefs,
    height = 500,
    enableExport = false,
    // File name (without extension) or a function returning one, evaluated at
    // export time so it can reflect current state (e.g. the selected dates).
    exportFileName = 'export',
    defaultColDef,
    onGridReady,
    onFirstDataRendered,
    onModelUpdated,
    pinnedBottomRowData,
    // Called with the row-data objects that survive the grid's filters (quick
    // filter + column filters), whenever that set changes — lets the caller
    // keep derived figures (the pinned total row) in step with what's shown.
    onVisibleRowsChanged,
    // Size columns to their content on load, then stretch them to fill any
    // leftover width. Opt out for grids that set their own widths.
    autoSizeColumns = true,
    ...props
}) {
    const apiRef = useRef(null);
    const gridWrapRef = useRef(null);
    const [quickFilter, setQuickFilter] = useState('');

    const isLive = () => {
        const api = apiRef.current;
        return api && !(typeof api.isDestroyed === 'function' && api.isDestroyed());
    };

    // The pinned total row is managed imperatively rather than passed as a prop
    // (see applyPinnedTo for the AG Grid 36 repaint bug this works around).
    const pinnedRef = useRef();
    pinnedRef.current = pinnedBottomRowData;
    const applyPinned = useCallback(() => {
        if (isLive()) applyPinnedTo(apiRef.current, pinnedRef.current);
    }, []);
    useEffect(() => { applyPinned(); }, [pinnedBottomRowData, applyPinned]);

    // Reports the post-filter row set upward. Compared by reference against the
    // last emission so sorts (same set, new order semantics aside) and pinned-
    // row updates don't cause render loops.
    const lastVisibleRef = useRef(null);
    const emitVisibleRows = useCallback(() => {
        if (!onVisibleRowsChanged || !isLive()) return;
        const out = [];
        apiRef.current.forEachNodeAfterFilter((n) => {
            if (n.data && !n.group && !n.detail) out.push(n.data);
        });
        const prev = lastVisibleRef.current;
        if (prev && prev.length === out.length && prev.every((r, i) => r === out[i])) return;
        lastVisibleRef.current = out;
        onVisibleRowsChanged(out);
    }, [onVisibleRowsChanged]);

    const handleModelUpdated = useCallback((e) => {
        emitVisibleRows();
        if (onModelUpdated) onModelUpdated(e);
    }, [emitVisibleRows, onModelUpdated]);

    const handleGridReady = useCallback((params) => {
        apiRef.current = params.api;
        applyPinned();
        if (onGridReady) onGridReady(params);
    }, [onGridReady, applyPinned]);

    // Two-pass sizing: measure each column against its content, then — only if
    // the result leaves dead space to the right — stretch the columns to fill
    // the grid. Content-first keeps narrow columns (counts, dates) narrow
    // instead of giving every column an equal share.
    const fitColumns = useCallback(() => {
        if (!autoSizeColumns || !isLive()) return;
        const api = apiRef.current;
        const cols = api.getColumns() || [];
        const sizable = cols.filter((c) => !c.getColDef().suppressAutoSize);
        if (!sizable.length) return;

        api.autoSizeColumns(sizable.map((c) => c.getColId()));

        const overflowing = sizable
            .filter((c) => c.getActualWidth() > MAX_AUTO_WIDTH)
            .map((c) => ({ key: c.getColId(), newWidth: MAX_AUTO_WIDTH }));
        if (overflowing.length) {
            if (typeof api.setColumnWidths === 'function') api.setColumnWidths(overflowing);
            else if (typeof api.setColumnWidth === 'function') {
                overflowing.forEach((c) => api.setColumnWidth(c.key, c.newWidth));
            }
        }

        const used = (api.getColumns() || []).reduce((sum, c) => sum + c.getActualWidth(), 0);
        const available = gridWrapRef.current ? gridWrapRef.current.clientWidth : 0;
        if (available && used < available) api.sizeColumnsToFit();
    }, [autoSizeColumns]);

    const handleFirstDataRendered = useCallback((params) => {
        fitColumns();
        if (onFirstDataRendered) onFirstDataRendered(params);
    }, [fitColumns, onFirstDataRendered]);

    // Re-fit when the data or the columns change (a re-run, a different report).
    // Deferred a frame so the grid has applied the new props before measuring.
    useEffect(() => {
        const id = requestAnimationFrame(() => fitColumns());
        return () => cancelAnimationFrame(id);
    }, [rowData, columnDefs, fitColumns]);

    const buildName = useCallback((ext) => {
        const raw = typeof exportFileName === 'function' ? exportFileName() : exportFileName;
        const base = String(raw || 'export').replace(/\.(csv|xlsx)$/i, '');
        return `${base}.${ext}`;
    }, [exportFileName]);

    // Export what the grid shows (dates as YYYY-MM-DD, etc.), except columns
    // flagged context.exportRaw (currency) which export their underlying number
    // so spreadsheets keep them numeric.
    const processCell = useCallback((p) => {
        const colDef = p.column.getColDef();
        if (colDef.context && colDef.context.exportRaw) return p.value;
        if (typeof colDef.valueFormatter === 'function') {
            return colDef.valueFormatter({
                value: p.value,
                data: p.node && p.node.data,
                node: p.node,
                colDef,
                column: p.column,
                api: p.api,
            });
        }
        return p.value;
    }, []);

    const handleExportCsv = useCallback(() => {
        apiRef.current?.exportDataAsCsv({ fileName: buildName('csv'), processCellCallback: processCell });
    }, [buildName, processCell]);

    const handleExportExcel = useCallback(() => {
        apiRef.current?.exportDataAsExcel({ fileName: buildName('xlsx'), processCellCallback: processCell });
    }, [buildName, processCell]);

    const onQuickFilterChange = useCallback((e) => {
        const value = e.target.value;
        setQuickFilter(value);
        apiRef.current?.setGridOption('quickFilterText', value);
    }, []);

    const mergedDefaultColDef = { ...BASE_DEFAULT_COL_DEF, ...defaultColDef };

    const grid = (
        <AgGridReact
            theme={themeQuartz}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={mergedDefaultColDef}
            cellSelection
            suppressAggFuncInHeader
            rowGroupPanelShow="always"
            sideBar={DEFAULT_SIDE_BAR}
            statusBar={DEFAULT_STATUS_BAR}
            onGridReady={handleGridReady}
            onModelUpdated={handleModelUpdated}
            onFirstDataRendered={handleFirstDataRendered}
            {...props}
        />
    );

    if (!enableExport) {
        return <div ref={gridWrapRef} style={{ height, width: '100%' }}>{grid}</div>;
    }

    return (
        <div style={{ height, width: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="datagrid__toolbar">
                <input
                    type="search"
                    className="datagrid__search"
                    placeholder="Search all columns…"
                    value={quickFilter}
                    onChange={onQuickFilterChange}
                />
                <div className="datagrid__toolbar-actions">
                    <button type="button" className="datagrid__export-btn" onClick={handleExportCsv}>⬇ CSV</button>
                    <button type="button" className="datagrid__export-btn" onClick={handleExportExcel}>⬇ Excel</button>
                </div>
            </div>
            <div ref={gridWrapRef} style={{ flex: 1, minHeight: 0 }}>{grid}</div>
        </div>
    );
}
